/* eslint-disable react/no-unescaped-entities */
import { useEffect } from 'react'
import { Typography, Divider, Row, Col, Card, Image, Button, Space } from 'antd' // Add Image import
import ReviewForm from './ReviewForm'
import Faq from './Faq'
// import Testimonials from './Testimonials' // Assuming Testimonials is not used based on previous steps
import Background from '../assets/cz_back.png' // Import the background image
import Logo from '../assets/colozoo_logo.svg' // Import the logo SVG
import review1en from '../assets/reviews/1-en.png'
import review2en from '../assets/reviews/2-en.png'
import review3en from '../assets/reviews/3-en.png'
import { useTranslation } from 'react-i18next'
import PageTitle from './PageTitle'

const { Title, Paragraph, Text, Link } = Typography

// Language switcher component
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const lngs: any = {
  de: { nativeName: 'Deutsch' },
  fr: { nativeName: 'Français' },
  it: { nativeName: 'Italiano' },
  es: { nativeName: 'Español' },
  en: { nativeName: 'English UK' },
  'en-US': { nativeName: 'English US' },
}

const LanguageSwitcher = () => {
  const { i18n } = useTranslation() 

  return (
    // Removed marginBottom from Space, will be handled by parent Row if needed
    <Space wrap style={{ display: 'flex', paddingTop: '16px' }}> 
      {Object.keys(lngs).map((lng) => (
        <Button
          key={lng}
          type={i18n.resolvedLanguage === lng ? 'primary' : 'default'}
          onClick={() => i18n.changeLanguage(lng)}
          size="small" // Make buttons smaller
          style={{ fontSize: '12px', padding: '0 8px' }} // Make text smaller and adjust padding
        >
          {lngs[lng].nativeName}
        </Button>
      ))}
    </Space>
  )
}

const Landing = () => {
  const { t, i18n } = useTranslation('landing') // Specify the 'landing' namespace

  useEffect(() => {
    document.title = t('docTitle')
    return () => {
      document.title = 'Colozoo'
    }
  }, [ t, i18n.language ]) // Add i18n.language to dependencies to update title on lang change


  return (
    <>
      <PageTitle title={t('meta.title') || 'Colozoo – Promotion'} />
      <div style={{
        minHeight: '100vh',      
        backgroundImage: `url(${Background})`,
        backgroundSize: 'cover',      
        backgroundPosition: 'center',      
        backgroundRepeat: 'no-repeat',      
        backgroundAttachment: 'fixed',      
        padding: '20px 0' 
      }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 20px' }}> 
          
          {/* Row for Logo and Language Switcher */}
          <Row justify="space-between" align="middle" style={{ marginBottom: '20px' }}>
            <Col>
              <Image src={Logo} alt="Colozoo Logo" preview={false} style={{ width: '130px' }} /> {/* Slightly smaller logo if needed */}
            </Col>
            <Col>
              <LanguageSwitcher />
            </Col>
          </Row>

          {/* Intro Section in a Card */}
          <Row justify="center" style={{ marginBottom: '30px' }}>
            <Col xs={24}>
              <Card>
                <div style={{ textAlign: 'center' }}> 
                  <Title level={1}>{t('pageTitle')}</Title>
                  <Paragraph>
                    {t('feedbackIntro')}
                  </Paragraph>
                  <Paragraph style={{ fontSize: '22px' }} strong>
                    {t('freeProductOffer')}
                  </Paragraph>
                </div>
              </Card>
            </Col>
          </Row>
    
          {/* Combined How-to (2 cols), Notes (2 cols), and Scroll Button Section */}
          <Row justify="center" style={{ marginBottom: '30px' }}>
            <Col xs={24}>
              <Card>
                <Row gutter={[ 16, 24 ]} style={{ marginBottom: '24px' }}> 
                  <Col xs={24} md={12}>
                    <Title level={3}>{t('howToClaimTitle')}</Title>
                    <Paragraph>
                      <ol>
                        <li>{t('howToClaimStep1')}
                          <Text strong> {t('howToClaimStep1Detail')}</Text></li>
                        <li>{t('howToClaimStep2')}</li>
                        <li>{t('howToClaimStep3')}
                          <ul>
                            <li>{t('howToClaimListItemReviewLink')}</li>
                            <li>{t('howToClaimListItemOrderNumber')}</li>
                            <li>{t('howToClaimListItemEmail')}</li>
                            <li>{t('howToClaimListItemShipping')}</li>
                            <li>{t('howToClaimListItemProductChoice')} <Link href={t('links.storeLink') as string} target="_blank" rel="noopener noreferrer">{t('amazonStoreLinkText')}</Link></li>
                          </ul>
                        </li>
                      </ol>
                    </Paragraph>
                  </Col>

                  <Col xs={24} md={12}>
                    <Title level={3}>{t('importantNotesTitle')}</Title>
                    <Paragraph>
                      <ul>
                        <li>{t('importantNotesItem1')}</li>
                        <li>{t('importantNotesItem2')}</li>
                        <li><strong>{t('importantNotesItem3')}</strong></li>
                        <li>{t('importantNotesItem4')}</li>
                        <li><strong>{t('importantNotesItem5')}</strong></li>
                      </ul>
                    </Paragraph>
                  </Col>
                </Row>

                <Divider />

                <div style={{ textAlign: 'center' }}> 
                  <Paragraph>
                    {t('skipToFormButtonText')}
                  </Paragraph>
                  <Button 
                    type="primary" 
                    size="large" 
                    onClick={() => {
                      const formElement = document.getElementById('review-form-section') 
                      if (formElement) {
                        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
                      }
                    }}
                  >
                    {t('goToClaimFormButton')}
                  </Button>
                </div>
              </Card>
            </Col>
          </Row>

          {/* FAQ Section */}
          <Row justify="center" style={{ marginBottom: '30px' }}> 
            <Col xs={24}> 
              <Card>
                <Title level={3}>{t('faqTitle')}</Title>
                <Faq /> 
              </Card>
            </Col>
          </Row>

          {/* Examples Section (already in a Card) */}
          <Row justify="center" style={{ marginBottom: '30px' }}>
            <Col xs={24}> 
              <Card>
                <Title level={4}>{t('examplesTitle')}</Title>
                <Image 
                  src={review1en} 
                  alt="Review Example 1" 
                  preview={true} 
                  style={{ width: '100%', marginBottom: '20px', cursor: 'pointer' }} 
                />
                <Link href={t('links.review1') as string} target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginBottom: '20px' }}>
                  {t('viewExampleReviewButton')} 1 {/* Adjust if you have specific keys for each example */}
                </Link>
                
                <Image 
                  src={review2en}
                  alt="Review Example 2" 
                  preview={true} 
                  style={{ width: '100%', marginBottom: '20px', cursor: 'pointer' }} 
                />
                <Link href={t('links.review2') as string} target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginBottom: '20px' }}>
                  {t('viewExampleReviewButton')} 2
                </Link>

                <Image 
                  src={review3en}
                  alt="Review Example 3" 
                  preview={true} 
                  style={{ width: '100%', marginBottom: '20px', cursor: 'pointer' }} 
                />
                <Link href={t('links.review3') as string} target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginBottom: '20px' }}>
                  {t('viewExampleReviewButton')} 2
                </Link>
              </Card>
            </Col>
          </Row>
          
          <div id="review-form-section" style={{ marginBottom: '30px' }}> 
            <Row justify="center">
              <Col xs={24}>
                <Card>
                  <div style={{ textAlign: 'center', maxWidth: '600px', margin: '0 auto', marginBottom: '20px' }}> 
                    <Text style={{ fontSize: '28px', marginBottom: '10px', display: 'block' }}>⭐⭐⭐⭐⭐</Text> 
                    <Title level={3}>{t('formTitle')}</Title>
                  </div>
                  
                  <ReviewForm />
                </Card>
              </Col>
            </Row>
          </div>
    
          <Row justify="center" style={{ marginTop: '30px', marginBottom: '20px' }}> 
            <Col xs={24}>
              <Card>
                <div style={{ textAlign: 'center', marginBottom: '15px' }}> 
                  <Paragraph>
                    {t('footerContact')}
                  </Paragraph>
                </div>

                <Divider /> 

                <div style={{ textAlign: 'center', marginBottom: '10px' }}> 
                  <Link href='/' target='_blank' rel="noopener noreferrer" style={{ color: 'grey', fontSize: 14, margin: '0 5px' }}>
                    {t('footerColozooWebsite')}
                  </Link>
                  <span style={{ color: 'grey', fontSize: 14 }}>|</span>
                  <Link href='/legal-notice/' target='_blank' rel="noopener noreferrer" style={{ color: 'grey', fontSize: 14, margin: '0 5px' }}>
                    {t('footerLegalNotice')}
                  </Link>
                  <span style={{ color: 'grey', fontSize: 14 }}>|</span>
                  <Link href='/privacy-policy/' target='_blank' rel="noopener noreferrer" style={{ color: 'grey', fontSize: 14, margin: '0 5px' }}>
                    {t('footerPrivacyPolicy')}
                  </Link>
                </div>
                {/* Note: The country list from the old footer_translations.countries is now part of importantNotesItem4 */}
              </Card>
            </Col>
          </Row>
    
        </div>
      </div>
    </>
  )
}

export default Landing